import React from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';

import { InputLabel, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import {
  RequiredIndicator,
  RadioOptions,
  Typeahead,
  FormErrorMessage
} from '@timmons-group/shared-react-components';
import { FIELD_TYPES } from '@timmons-group/shared-react-components/constants';
import { Box } from '@mui/material';

const AnyField = ({ control, rules, layout, disabled, tempDisable, ...props }) => {
  const renderState = renderType(layout, disabled, tempDisable);

  return (
    <Box {...props}>
      <Controller
        control={control}
        rules={rules}
        name={layout.name}
        render={renderState}
      />
    </Box>
  );
}

const renderType = (layout, disabled, tempDisable) => {
  const { id, type, name, label, options } = layout;
  switch (type) {
    case FIELD_TYPES.DATE: {
      const renderDate = ({ field: { value, onChange }, fieldState: { error } }) => (
        <>
          <DatePicker
            name={name}
            disabled={disabled}
            id={id}
            value={value}
            onChange={onChange}
            renderInput={(params) => (
              <>
                <InputLabel disabled={disabled} htmlFor={id || name} error={!!error}><RequiredIndicator isRequired={!!layout.required} />{label}</InputLabel>
                <TextField sx={{ width: '100%' }} {...params} />
              </>
            )}
          />
          <FormErrorMessage error={error} />
        </>
      );
      return renderDate;
    }
    case FIELD_TYPES.TEXT:
    case FIELD_TYPES.LONG_TEXT:
    case FIELD_TYPES.INT:
    case FIELD_TYPES.LINK:
    case FIELD_TYPES.FLOAT: {
      const isMultiple = type === FIELD_TYPES.LONG_TEXT;
      const renderTextField = ({ field: { value, onChange }, fieldState: { error } }) => (
        <>
          <InputLabel htmlFor={id || name} error={!!error}><RequiredIndicator isRequired={!!layout.required} />{label}</InputLabel>
          <TextField sx={{ width: '100%' }}
            id={id || name}
            error={!!error}
            onChange={onChange}
            value={value}
            multiline={isMultiple}
            minRows={isMultiple ? 3 : 1}
            placeholder={layout.placeholder || `Enter ${label}`}
            variant="outlined"
          />
          {/* Temp hack on spacing - need to remove space on Typeahead in SRC */}
          <FormErrorMessage error={error} sx={{ marginLeft: '14px' }} />
        </>
      );
      return renderTextField;
    }
    case FIELD_TYPES.CHOICE:
    case FIELD_TYPES.OBJECT: {
      const renderTypeahead = ({ field, field: { onChange }, fieldState: { error } }) => {
        // Temp hack specifically for tribal field on indiv recipient
        // We need to upgrade to generic form anyways so no point debugging AnyField further IMO
        // This works for now but is not scaleable, nor is it a good practice, apologies 🙏
        // tempDisable is prop drilled all the way from RecipientForm component 🤮
        const temp = field?.name === 'tribal' && tempDisable ? true : false;
        // value is passed in via the react hook form inside of field
        // Ref is needed by the typeahead / autoComplete component and is passed in via props spreading
        return (
          // We need to manually connect a few props here for react hook form
          <Typeahead
            {...field}
            sx={{ width: '100%' }}
            disabled={layout.disabled || temp}
            items={layout.choices}
            label={label}
            isRequired={!!layout.required}
            // These are props that are passed to the MUI TextField rendered by Typeahead
            textFieldProps={{
              placeholder: layout.placeholder || `Select ${label}`,
              error: !!error,
              helperText: error?.message,
            }}
            // hooks-form appears to only want value and not the native onChange
            onChange={(_, newValue) => {
              onChange(newValue)
            }}
            error={error}
          />
        )
      };
      return renderTypeahead;
    }
    case 'radio': {
      const renderRadio = ({ field: { value, onChange }, fieldState: { error } }) => {
        // so we need to manually connect a few props here for react hook form
        return (
          <RadioOptions
            items={options}
            isRequired={true}
            id={id}
            label={label}
            value={value}
            onChange={onChange}
            error={error}
          />);
      };
      return renderRadio;
    }
    default:
      return <TextField />;
  }
}

AnyField.propTypes = {
  control: PropTypes.object.isRequired,
  rules: PropTypes.object,
  layout: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
}

export default AnyField;
